import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { IconButton } from "../IconButton/IconButton.js";
import { useControllable } from "../../lib/useControllable.js";
import { useFormFieldControl } from "../FormField/FormFieldContext.js";
import { Input } from "../Input/Input.js";
import { ListboxPopup, filterOptions, nextActive } from "../_listbox/Listbox.js";
/* empty css                 */
import { forwardRef, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/SearchSelect/SearchSelect.tsx
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
/**
* A text-filterable single select: an editable combobox with a listbox popup. Closed,
* the field shows the selected option's label; open, it takes the filter text. Options
* can come from the server (`filter={false}`, `onInputChange`, `loading`). The ref goes
* to the text field; `className` goes on the wrapper.
*/
var SearchSelect = forwardRef(function SearchSelect({ options, value = null, onChange, placeholder = "Search…", size = "md", invalid: invalidProp, disabled = false, emptyMessage = "No matches", id: idProp, className, filter = true, loading = false, loadingMessage = "Loading…", freeText = false, onClear, open: openProp, defaultOpen = false, onOpenChange, inputValue: inputValueProp, defaultInputValue = "", onInputChange, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": describedByProp, "aria-required": ariaRequired }, ref) {
	const generatedId = useId();
	const { id = `ds-searchselect-${generatedId}` } = useFormFieldControl({ id: idProp });
	const listboxId = `${id}-listbox`;
	const [openState, setOpen] = useControllable(openProp, defaultOpen, onOpenChange);
	const open = openState && !disabled;
	const [query, setQuery] = useControllable(inputValueProp, defaultInputValue, onInputChange);
	const [activeValue, setActiveValue] = useState(null);
	const wrapperRef = useRef(null);
	const inputRef = useRef(null);
	const selected = value === null ? void 0 : options.find((option) => option.value === value);
	const selectedLabel = value === null ? "" : selected?.label ?? value;
	const filtered = useMemo(() => filter ? filterOptions(options, query) : options, [
		filter,
		options,
		query
	]);
	const shown = loading ? [] : filtered;
	const enabled = shown.filter((option) => !option.disabled);
	const active = enabled.some((option) => option.value === activeValue) ? activeValue : null;
	const optionId = (option) => `${id}-option-${options.findIndex((other) => other.value === option.value)}`;
	const [labelText, setLabelText] = useState();
	useLayoutEffect(() => {
		if (open) setLabelText(inputRef.current?.labels?.[0]?.textContent?.trim() || void 0);
	}, [open]);
	/** Opens the list with the filter cleared, highlighting the selected option or an end. */
	const openList = (highlight) => {
		if (!open) {
			setOpen(true);
			if (query) setQuery("");
		}
		const all = filter ? options.filter((option) => !option.disabled) : enabled;
		if (highlight === "selected") setActiveValue(selected && !selected.disabled ? selected.value : all[0]?.value ?? null);
		else if (highlight === "first") setActiveValue(all[0]?.value ?? null);
		else if (highlight === "last") setActiveValue(all.at(-1)?.value ?? null);
	};
	/** Closes the list and drops the filter text: the field shows the selection again. */
	const closeList = () => {
		if (open) setOpen(false);
		if (query) setQuery("");
		setActiveValue(null);
	};
	const commit = (next) => {
		onChange(next);
		closeList();
	};
	const handleKeyDown = (event) => {
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (!open) openList(event.altKey ? "none" : "selected");
				else setActiveValue(nextActive(enabled, active, 1));
				break;
			case "ArrowUp":
				event.preventDefault();
				if (event.altKey) closeList();
				else if (!open) openList(selected ? "selected" : "last");
				else setActiveValue(nextActive(enabled, active, -1));
				break;
			case "Enter": {
				if (!open) break;
				event.preventDefault();
				const option = enabled.find((other) => other.value === active);
				const text = query.trim();
				if (option) commit(option.value);
				else if (freeText && text) commit(text);
				break;
			}
			case "Escape": if (open) {
				event.preventDefault();
				closeList();
			}
		}
	};
	const handleChange = (event) => {
		setQuery(event.target.value);
		if (!open) setOpen(true);
		if (freeText) {
			setActiveValue(null);
			return;
		}
		const matches = (filter ? filterOptions(options, event.target.value) : options).filter((option) => !option.disabled);
		setActiveValue(event.target.value.trim() ? matches[0]?.value ?? null : null);
	};
	const handleMouseDown = (event) => {
		if (disabled || event.target.closest("button")) return;
		const onField = event.target === inputRef.current;
		if (!open) openList("selected");
		else if (!onField) closeList();
	};
	const setInput = (element) => {
		inputRef.current = element;
		setRef(ref, element);
	};
	const statusText = !open ? "" : loading ? loadingMessage : shown.length === 0 ? emptyMessage : `${shown.length} ${shown.length === 1 ? "option" : "options"}`;
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("div", {
		ref: wrapperRef,
		className: cx("ds-searchselect", open && "ds-searchselect--open", className),
		onMouseDown: handleMouseDown,
		children: [/* @__PURE__ */ jsx(Input, {
			ref: setInput,
			id,
			size,
			...invalidProp !== void 0 && { invalid: invalidProp },
			disabled,
			type: "text",
			role: "combobox",
			autoComplete: "off",
			"aria-autocomplete": "list",
			"aria-expanded": open,
			"aria-controls": open ? listboxId : void 0,
			"aria-activedescendant": open && active !== null ? optionId(enabled.find((option) => option.value === active)) : void 0,
			"aria-label": ariaLabel,
			"aria-labelledby": ariaLabelledBy,
			"aria-describedby": describedByProp,
			"aria-required": ariaRequired,
			"aria-busy": open && loading || void 0,
			placeholder: selectedLabel || placeholder,
			value: open ? query : selectedLabel,
			onChange: handleChange,
			onKeyDown: handleKeyDown,
			onBlur: (event) => {
				if (!wrapperRef.current?.contains(event.relatedTarget)) closeList();
			},
			leading: /* @__PURE__ */ jsx(Icon, { name: "search" }),
			trailing: /* @__PURE__ */ jsxs(Fragment$1, { children: [onClear && value !== null && !disabled && /* @__PURE__ */ jsx(IconButton, {
				icon: "x",
				size: "sm",
				"aria-label": selectedLabel ? `Clear ${selectedLabel}` : "Clear",
				onClick: () => {
					onClear();
					inputRef.current?.focus();
				}
			}), /* @__PURE__ */ jsx(Icon, {
				name: "chevron-down",
				className: "ds-searchselect__chevron"
			})] })
		}), /* @__PURE__ */ jsx(VisuallyHidden, {
			role: "status",
			children: statusText
		})]
	}), open && /* @__PURE__ */ jsx(ListboxPopup, {
		id: listboxId,
		anchorRef: wrapperRef,
		options: shown,
		optionId,
		activeValue: active,
		isSelected: (v) => v === value,
		emptyMessage,
		loadingMessage: loading ? loadingMessage : void 0,
		onSelect: (option) => commit(option.value),
		onActiveChange: setActiveValue,
		onDismiss: closeList,
		"aria-label": ariaLabel ?? (ariaLabelledBy ? void 0 : labelText),
		"aria-labelledby": ariaLabelledBy
	})] });
});
//#endregion
export { SearchSelect };

//# sourceMappingURL=SearchSelect.js.map